// Rajd przygodowy /ar/{alias} (makiety „Wyniki – makiety”: AR1–AR3, AR5) — czysta logika, bez Vue.
// Liczone raz na pobranie /splits (jak utils/results-list.js dla /{alias}), a nie w szablonie.
//
// - Etapy z markerów: strefy zmian (typ 91) dzielą trasę, meta 8/93 kończy ostatni etap, starty 1/7
//   nie są PK. Bez strefy = jeden etap (wariant AR5). Ta sama zasada co beta (stageUnits/stageTimes).
// - Nazwa i dyscyplina etapu: markers.stage_name / stage_discipline na markerze, od którego etap się
//   ZACZYNA — start 1/7 dla 1. etapu (jest tylko w /events/alias, bo /splits ma markery z splits = 1),
//   strefa 91 dla kolejnych. Bez nich „Etap N” i dyscyplina z typów PK (101 rower, 102 trekking,
//   103 kajak, inaczej 104 zadanie specjalne, 94 zadanie) — jak w becie.
// - PK w 3 stanach: zaliczony / pominięty (drużyna zamknęła już ten etap: podbiła jego strefę albo metę,
//   ma podbicie dalszego etapu albo jest na mecie) / jeszcze przed drużyną. U drużyny w trasie i DNF braki
//   w bieżącym etapie są „jeszcze przed”, bo w etapie kolejność bywa dowolna.
// - Czas etapu brutto: między podbiciami stref (start → strefa 1, strefa 1 → strefa 2, …, ostatnia
//   strefa → meta). Miejsce na etapie tylko dla drużyn z kompletem PK etapu (także DNF), bez obsługi
//   i „poza klasyfikacją”; najszybszy = 1. miejsce.
// - Czas całkowity = meta − start + kara (penalty_seconds). Kolejność: AR_ORDER niżej.
// - Wszystkie czasy przez parseApiTime (czas polski, także z „Z”), godziny w strefie Europe/Warsaw —
//   wynik nie zależy od strefy przeglądarki.
import { isClassified, isSupportRole } from './runner-role.js'
import { formatDuration, parseApiTime } from './polish-time.js'
import { searchKey } from './results-list.js'

export const ZONE_TYPE = 91
export const TASK_TYPE = 94
const START_TYPES = new Set([1, 7])
const FINISH_TYPES = new Set([8, 93])

// Kolejność na mecie (makieta AR1, na kanwie „Do decyzji 1” — do potwierdzenia przed wdrożeniem):
// 'checkpoints' = najpierw liczba zaliczonych PK, potem czas z karą (jak w regulaminach rajdów);
// 'time' = sam czas z karą (tak liczyło dawne /ar przy kilku etapach, ale bez kary).
// Wydarzenie z punktami (runners.score > 0, wariant AR5): punkty, potem czas — niezależnie od tej stałej.
export const AR_ORDER = 'checkpoints'

// Te same kody co sledz-api Marker::STAGE_DISCIPLINES i sledz-app utils/stageTimes.js
export const STAGE_DISCIPLINES = ['trekking', 'run', 'bike', 'kayak', 'packraft', 'swim', 'ropes', 'orienteering', 'special', 'task', 'other']
// Nazwy jak w becie (sledz-app i18n pl stageTimes.disciplines)
export const DISCIPLINE_LABELS = Object.freeze({
  trekking: 'Trekking',
  run: 'Bieg',
  bike: 'Rower',
  kayak: 'Kajak',
  packraft: 'Packraft',
  swim: 'Pływanie',
  ropes: 'Liny',
  orienteering: 'Na orientację',
  special: 'Zadanie specjalne',
  task: 'Zadanie',
  other: 'Inna'
})
const DISCIPLINE_BY_TYPE = { 101: 'bike', 102: 'trekking', 103: 'kayak' }
const EXTRA_DISCIPLINE_BY_TYPE = { 104: 'special', 94: 'task' }

const TIME_ZONE = 'Europe/Warsaw'
const weekdayFormat = new Intl.DateTimeFormat('pl-PL', { timeZone: TIME_ZONE, weekday: 'short' })
const hourMinuteFormat = new Intl.DateTimeFormat('pl-PL', { timeZone: TIME_ZONE, hour: 'numeric', minute: '2-digit' })
const dayKeyFormat = new Intl.DateTimeFormat('en-CA', { timeZone: TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit' })

const typeOf = marker => Number(marker?.type)
const bySort = (a, b) => (Number(a.sort) || 0) - (Number(b.sort) || 0)

// "13:04"
export function clockHM(ms) {
  return ms == null ? '' : hourMinuteFormat.format(new Date(ms))
}

// "sob. 15:20" — z dniem tygodnia, gdy rajd trwa więcej niż jeden dzień (multiDay), inaczej "15:20"
export function clockWithDay(ms, multiDay) {
  if (ms == null) return ''
  return multiDay ? `${weekdayFormat.format(new Date(ms))} ${clockHM(ms)}` : clockHM(ms)
}

export function isMultiDay(startMs, endMs) {
  return startMs != null && endMs != null && dayKeyFormat.format(new Date(startMs)) !== dayKeyFormat.format(new Date(endMs))
}

// Strata ze znakiem, zawsze z godzinami (przy czasach rajdu „+24:20” czytałoby się jak doba): "+1:42:55",
// "+0:24:20"; drużyna z mniejszą liczbą PK bywa szybsza niż lider: "−0:12:30"
export function formatSignedGap(ms) {
  if (ms == null || !Number.isFinite(ms) || Math.round(ms / 1000) === 0) return ''
  const sign = ms > 0 ? '+' : '−'
  return `${sign}${formatDuration(Math.abs(ms))}`
}

// Pierwsza linia nazwy markera ("PK3|Szczyt" → "PK3")
export function markerName(marker) {
  return String(marker?.text || '').split(/\r?\n|\|/)[0].trim()
}

// Tekst w kwadracie PK: "PK7", "PK 7", "CP12" → "7"/"12"; krótka nazwa (do 3 znaków) bez zmian; inaczej pusto
export function checkpointLabel(marker) {
  const name = markerName(marker)
  const m = /^(?:PK|CP)\s*(\d+)$/i.exec(name)
  if (m) return m[1]
  return name.length > 0 && name.length < 4 ? name : ''
}

/** Dyscyplina zgadnięta z typów PK etapu (jak sledz-app defaultStageDiscipline) albo null. */
export function guessDiscipline(points) {
  const counts = new Map()
  for (const p of points || []) {
    const d = DISCIPLINE_BY_TYPE[typeOf(p)]
    if (d) counts.set(d, (counts.get(d) || 0) + 1)
  }
  let best = null
  let bestCount = 0
  counts.forEach((count, d) => {
    if (count > bestCount) [best, bestCount] = [d, count]
  })
  if (best) return best
  for (const type of [104, 94]) {
    if ((points || []).some(p => typeOf(p) === type)) return EXTRA_DISCIPLINE_BY_TYPE[type]
  }
  return null
}

/**
 * Etapy wydarzenia.
 * markers: markery z /splits (te, do których odnoszą się podbicia); eventMarkers: markery z /events/alias
 * (mają też start bez podbić — nazwa i dyscyplina 1. etapu).
 * -> { stages: [{ index, points, end, endIsZone, name, discipline, label }], hasZones, totalPoints }
 */
export function buildArStages(markers, eventMarkers = []) {
  const list = (markers || []).filter(Boolean)
  const extra = (eventMarkers || []).filter(m => m && !list.some(l => String(l.id) === String(m.id)))
  const byId = new Map((eventMarkers || []).filter(Boolean).map(m => [String(m.id), m]))
  // /splits oddaje cały model markera, /events/alias jawną listę pól — pole bierzemy z tego, które je ma
  const field = (marker, key) => (marker ? marker[key] ?? byId.get(String(marker.id))?.[key] ?? null : null)
  const hasStageFields = marker => Boolean(String(field(marker, 'stage_name') ?? '').trim() || field(marker, 'stage_discipline'))

  const sorted = [...list].sort(bySort)
  const raw = []
  let current = []
  for (const marker of sorted) {
    const type = typeOf(marker)
    if (START_TYPES.has(type)) continue
    if (type === ZONE_TYPE) {
      if (current.length) raw.push({ points: current, end: marker })
      current = []
    } else if (FINISH_TYPES.has(type)) {
      if (current.length) raw.push({ points: current, end: marker })
      current = []
    } else {
      current.push(marker)
    }
  }
  if (current.length) raw.push({ points: current, end: null })
  // bez stref zmian = jeden etap (AR5), nawet gdy po mecie są jeszcze punkty
  if (raw.length > 1 && !raw.some(s => s.end && typeOf(s.end) === ZONE_TYPE)) {
    const finish = raw.map(s => s.end).find(Boolean) || null
    raw.splice(0, raw.length, { points: raw.flatMap(s => s.points), end: finish })
  }

  const all = [...list, ...extra].sort(bySort)
  const startMarker = all.find(m => START_TYPES.has(typeOf(m))) || null
  // Marker otwierający etap: start / strefa kończąca poprzedni etap, a także strefy bez PK przed sobą
  // (dwie strefy z rzędu — wejście i wyjście). Wygrywa pierwszy z ustawioną nazwą albo dyscypliną.
  const openersOf = index => {
    const first = raw[index].points[0]
    const from = index === 0 ? -Infinity : Number(raw[index - 1].end?.sort) || 0
    const to = first ? Number(first.sort) || 0 : Infinity
    const zones = all.filter(m => typeOf(m) === ZONE_TYPE && (Number(m.sort) || 0) > from && (Number(m.sort) || 0) < to)
    const main = index === 0 ? startMarker : raw[index - 1].end
    return [main, ...zones].filter(Boolean)
  }

  const stages = raw.map((stage, index) => {
    const candidates = openersOf(index)
    const opener = candidates.find(hasStageFields) || candidates[0] || null
    const ownName = String(field(opener, 'stage_name') ?? '').trim()
    const ownDiscipline = STAGE_DISCIPLINES.includes(field(opener, 'stage_discipline')) ? field(opener, 'stage_discipline') : null
    const discipline = ownDiscipline || guessDiscipline(stage.points)
    return {
      index,
      points: stage.points,
      end: stage.end,
      endIsZone: Boolean(stage.end) && typeOf(stage.end) === ZONE_TYPE,
      name: ownName || null,
      discipline,
      // druga linia nagłówka kolumny: nazwa etapu z panelu albo nazwa dyscypliny
      label: ownName || (discipline ? DISCIPLINE_LABELS[discipline] : '')
    }
  })
  return {
    stages,
    hasZones: stages.some(s => s.endIsZone),
    totalPoints: stages.reduce((sum, s) => sum + s.points.length, 0)
  }
}

function groupOf(runner) {
  if (runner.abs) return 'dns'
  if (runner.dnf) return 'dnf'
  if (runner.finish_time || runner.finish_time_gps) return 'finished'
  return 'course'
}

function punchMs(runner, marker) {
  const split = marker ? runner.splits?.[marker.id] : null
  return split ? parseApiTime(split.time) : null
}

function lineupOf(runner) {
  const members = Array.isArray(runner.team_members) ? runner.team_members : []
  const names = members
    .map(m => [m?.first_name ?? m?.firstName, m?.second_name ?? m?.secondName].filter(Boolean).join(' ').trim())
    .filter(Boolean)
  return names.length ? names : null
}

// Czas mety: oficjalny (finish_time), wstępny z GPS (finish_time_gps), podbicie mety
function finishMsOf(runner, finishMarker) {
  return parseApiTime(runner.finish_time) ?? parseApiTime(runner.finish_time_gps) ?? punchMs(runner, finishMarker)
}

/**
 * Wiersze /ar.
 * results: wyniki z magazynu (runner.splits = {markerId: split}, penaltySeconds, pointScore);
 * plan: buildArStages(); opts: categories [{id, name}], eventStartMs, eventEndMs, nowMs, order.
 * -> { rows, stageBest: [ms|null], multiDay, hasPoints }
 */
export function buildArRows(results, plan, { categories = [], eventStartMs = null, eventEndMs = null, nowMs = Date.now(), order = AR_ORDER } = {}) {
  const stages = plan?.stages || []
  const totalPoints = plan?.totalPoints || 0
  const multiDay = isMultiDay(eventStartMs, eventEndMs)
  const categoryName = new Map((categories || []).map(c => [String(c.id), c.name || '']))
  const list = (results || []).filter(r => r && !isSupportRole(r))
  const hasPoints = list.some(r => Number(r.pointScore) > 0)
  const lastStage = stages[stages.length - 1]
  const finishMarker = lastStage?.end && !lastStage.endIsZone ? lastStage.end : null

  const rows = list.map(r => {
    const group = groupOf(r)
    const startMs = parseApiTime(r.start_time) ?? eventStartMs
    const finishMs = group === 'finished' ? finishMsOf(r, finishMarker) : null
    const penaltyMs = r.penaltySeconds > 0 ? r.penaltySeconds * 1000 : 0
    const timeMs = finishMs != null && startMs != null ? Math.max(0, finishMs - startMs) + penaltyMs : null

    // ---- etapy ----
    let anchorMs = startMs
    const cells = stages.map(stage => {
      const isLast = stage === lastStage
      let endMs = stage.end ? punchMs(r, stage.end) : null
      if (isLast && !stage.endIsZone && finishMs != null) endMs = finishMs
      const done = stage.points.filter(p => r.splits?.[p.id])
      const stageStart = anchorMs
      const cell = {
        startMs: stageStart,
        endMs,
        timeMs: stageStart != null && endMs != null && endMs > stageStart ? endMs - stageStart : null,
        doneCount: done.length,
        total: stage.points.length,
        complete: done.length === stage.points.length,
        closed: endMs != null,
        points: stage.points.map(p => ({ id: p.id, ms: punchMs(r, p) })),
        place: null,
        best: false,
        current: false
      }
      anchorMs = endMs
      return cell
    })
    // etap zamknięty także wtedy, gdy drużyna ma podbicie któregoś dalszego etapu albo jest na mecie
    for (let i = cells.length - 1, laterPunch = group === 'finished'; i >= 0; i -= 1) {
      if (laterPunch) cells[i].closed = true
      if (cells[i].doneCount > 0 || cells[i].endMs != null) laterPunch = true
    }
    cells.forEach(cell => {
      cell.missing = cell.closed ? cell.total - cell.doneCount : 0
      cell.states = cell.points.map(p => (p.ms != null || r.splits?.[p.id] ? 'done' : cell.closed ? 'skipped' : 'pending'))
    })
    const punched = cells.reduce((sum, c) => sum + c.doneCount, 0)

    // ostatnie podbicie (PK albo strefa) — „PK23 13:04”, „wycofani po PK11”
    let last = null
    stages.forEach(stage => {
      for (const m of [...stage.points, stage.end].filter(Boolean)) {
        const ms = punchMs(r, m)
        if (ms != null && (!last || ms > last.ms)) last = { marker: m, ms }
      }
    })
    // bieżący etap: pierwszy niezamknięty (w trasie i DNF)
    let currentIndex = null
    if (group === 'course' || group === 'dnf') {
      const idx = cells.findIndex(c => !c.closed)
      currentIndex = idx >= 0 ? idx : null
      if (currentIndex != null && group === 'course') cells[currentIndex].current = true
    }

    const members = lineupOf(r)
    const catId = r.category_id != null ? String(r.category_id) : null
    return {
      id: r.id,
      number: r.number,
      name: r.name || '',
      bio: r.bio || '',
      members,
      lineup: members ? members.join(' · ') : (r.bio || ''),
      gender: r.gender === 'K' || r.gender === 'M' ? r.gender : null,
      categoryId: catId,
      categoryName: catId ? categoryName.get(catId) || '' : '',
      categoryPlace: null,
      group,
      classified: isClassified(r),
      place: null,
      outOfCompetition: group === 'finished' && !isClassified(r),
      points: hasPoints ? Number(r.pointScore) || 0 : null,
      punched,
      totalPoints,
      startMs,
      finishMs,
      timeMs,
      timeText: timeMs != null ? formatDuration(timeMs) : '',
      penaltyMin: penaltyMs > 0 ? Math.round(penaltyMs / 60000) : 0,
      penaltyComment: r.penaltyComment || r.penalty_comment || '',
      gpsOnly: group === 'finished' && !r.finish_time && !!r.finish_time_gps,
      gapText: '',
      cells,
      currentIndex,
      lastName: last ? markerName(last.marker) : '',
      lastMs: last ? last.ms : null,
      // dzień tygodnia tylko, gdy podbicie było innego dnia niż dziś ("PK23 13:04" / "PK19 sob. 22:40")
      lastClock: last ? clockWithDay(last.ms, dayKeyFormat.format(new Date(last.ms)) !== dayKeyFormat.format(new Date(nowMs))) : '',
      search: searchKey(`${r.name || ''} ${r.number ?? ''} ${r.bio || ''} ${members ? members.join(' ') : ''}`),
      numberText: String(r.number ?? '')
    }
  })

  // ---- kolejność ----
  const compareFinished = (a, b) => {
    if (hasPoints && a.points !== b.points) return b.points - a.points
    if (!hasPoints && order === 'checkpoints' && a.punched !== b.punched) return b.punched - a.punched
    if (a.timeMs != null && b.timeMs != null && a.timeMs !== b.timeMs) return a.timeMs - b.timeMs
    if (a.timeMs != null && b.timeMs == null) return -1
    if (a.timeMs == null && b.timeMs != null) return 1
    return (Number(a.number) || 0) - (Number(b.number) || 0)
  }
  const compareProgress = (a, b) => {
    if (a.punched !== b.punched) return b.punched - a.punched
    const aLast = a.lastMs ?? Infinity
    const bLast = b.lastMs ?? Infinity
    if (aLast !== bLast) return aLast - bLast
    return (Number(a.number) || 0) - (Number(b.number) || 0)
  }
  const groupRank = { finished: 0, course: 1, dnf: 2, dns: 3 }
  rows.sort((a, b) => {
    if (a.group !== b.group) return groupRank[a.group] - groupRank[b.group]
    if (a.group === 'finished') return compareFinished(a, b)
    if (a.group === 'dns') return (Number(a.number) || 0) - (Number(b.number) || 0)
    return compareProgress(a, b)
  })

  // ---- miejsca (tylko klasyfikowani na mecie; remis = ta sama liczba PK/punktów i czas) ----
  let place = 0
  let prev = null
  const categoryCounts = new Map()
  const leader = rows.find(row => row.group === 'finished' && row.classified) || null
  rows.forEach(row => {
    if (row.group !== 'finished' || !row.classified) return
    place += 1
    const tie = prev && prev.timeMs === row.timeMs && prev.punched === row.punched && prev.points === row.points
    row.place = tie ? prev.place : place
    prev = row
    if (row.categoryId) {
      // remis w kategorii też dzieli miejsce
      const c = categoryCounts.get(row.categoryId) || { n: 0, last: null }
      c.n += 1
      const catTie = c.last && c.last.timeMs === row.timeMs && c.last.punched === row.punched && c.last.points === row.points
      row.categoryPlace = catTie ? c.last.categoryPlace : c.n
      c.last = row
      categoryCounts.set(row.categoryId, c)
    }
    if (row !== leader) {
      if (hasPoints && leader.points !== row.points) row.gapText = `−${leader.points - row.points} pkt`
      else if (leader.timeMs != null && row.timeMs != null) row.gapText = formatSignedGap(row.timeMs - leader.timeMs)
    }
  })

  // ---- miejsca na etapach ----
  const stageBest = stages.map((stage, i) => {
    const ranked = rows
      .filter(row => row.classified && row.group !== 'dns' && row.cells[i].complete && row.cells[i].timeMs != null)
      .sort((a, b) => a.cells[i].timeMs - b.cells[i].timeMs)
    let p = 0
    let prevMs = null
    let prevPlace = null
    ranked.forEach(row => {
      p += 1
      const cell = row.cells[i]
      cell.place = prevMs === cell.timeMs ? prevPlace : p
      cell.best = cell.place === 1
      prevMs = cell.timeMs
      prevPlace = cell.place
    })
    return ranked.length ? ranked[0].cells[i].timeMs : null
  })

  // godziny wejścia do strefy / mety w rozwinięciu (z dniem przy rajdzie wielodniowym)
  rows.forEach(row => {
    row.cells.forEach(cell => {
      cell.timeText = cell.timeMs != null ? formatDuration(cell.timeMs) : ''
      cell.endClock = clockWithDay(cell.endMs, multiDay)
      cell.startClock = clockWithDay(cell.startMs, multiDay)
    })
  })

  return { rows, stageBest, multiDay, hasPoints }
}

/** Filtrowanie bez zmiany miejsc. query: numer (początek) albo fragment nazwy drużyny / składu. */
export function filterArRows(rows, { query = '', gender = 'all', category = 'all' } = {}) {
  const q = searchKey(query)
  const numeric = /^\d+$/.test(q)
  return rows.filter(row => {
    if (gender !== 'all' && row.gender !== gender) return false
    if (category !== 'all' && row.categoryId !== category) return false
    if (!q) return true
    return numeric ? row.numberText.startsWith(q) || row.search.includes(q) : row.search.includes(q)
  })
}

/** Dyscypliny etapów (bez powtórzeń, w kolejności trasy) — do legendy. */
export function stageDisciplines(stages) {
  const seen = []
  ;(stages || []).forEach(s => {
    if (s.discipline && !seen.includes(s.discipline)) seen.push(s.discipline)
  })
  return seen
}
